/**
 * Agency staff personas used by the sim IdP persona picker, the server seed and tests.
 *
 * Every persona has a stable OIDC subject (`sim|<slug>`). Agency users additionally have a stable
 * UserId. The `not_invited` persona exists in the IdP only, so signing in as them exercises the
 * "identity not invited" denial path (spec section 2: an email domain alone does not authorize).
 */
import type { UserId } from '../ids.js';
import type { Capability, Role } from '../roles.js';
import { uuidFromSeed } from './prng.js';

export const STAFF_EMAIL_DOMAIN = 'getagency.com';

export const STAFF_STATUSES = ['active', 'suspended', 'invited', 'not_invited'] as const;
export type StaffStatus = (typeof STAFF_STATUSES)[number];

export type StaffCohort = 'cohort_3' | 'cohort_10' | 'all';

export interface StaffPersona {
  /** URL-safe identifier, also the tail of the OIDC subject. */
  slug: string;
  /** Immutable IdP subject: `sim|<slug>`. */
  subject: string;
  email: string;
  displayName: string;
  givenName: string;
  familyName: string;
  title: string;
  roles: readonly Role[];
  /** Explicit per-user grants beyond role presets (never email.send; see directSend). */
  capabilityGrants: readonly Capability[];
  /** Direct-send permission (email.send). Default off per spec section 2. */
  directSend: boolean;
  status: StaffStatus;
  /** Stable Agency user id; null for the IdP-only persona. */
  userId: UserId | null;
  /** Slug of the administrator who invited this user; null for the founding administrator. */
  invitedBySlug: string | null;
  /** Release cohort used by the stub release policy. */
  cohort: StaffCohort;
}

interface PersonaInput {
  slug: string;
  givenName: string;
  familyName: string;
  email?: string;
  title: string;
  roles: readonly Role[];
  capabilityGrants?: readonly Capability[];
  directSend?: boolean;
  status?: StaffStatus;
  invitedBySlug?: string | null;
  cohort: StaffCohort;
}

function persona(input: PersonaInput): StaffPersona {
  const status = input.status ?? 'active';
  return {
    slug: input.slug,
    subject: `sim|${input.slug}`,
    email: input.email ?? `${input.slug.replace(/-/g, '.')}@${STAFF_EMAIL_DOMAIN}`,
    displayName: `${input.givenName} ${input.familyName}`,
    givenName: input.givenName,
    familyName: input.familyName,
    title: input.title,
    roles: input.roles,
    capabilityGrants: input.capabilityGrants ?? [],
    directSend: input.directSend ?? false,
    status,
    userId: status === 'not_invited' ? null : (uuidFromSeed(`user:${input.slug}`) as UserId),
    invitedBySlug: input.invitedBySlug === undefined ? 'amir-tarighat' : input.invitedBySlug,
    cohort: input.cohort,
  };
}

/** All IdP personas, including the not-invited one. Order is the persona-picker order. */
export const STAFF_PERSONAS: readonly StaffPersona[] = [
  persona({
    slug: 'amir-tarighat',
    givenName: 'Amir',
    familyName: 'Tarighat',
    email: 'amir@getagency.com',
    title: 'Agency administrator',
    roles: ['agency_administrator', 'forward_deployed_operator', 'template_publisher', 'audit_reviewer'],
    capabilityGrants: ['credential.debug'],
    directSend: true,
    invitedBySlug: null,
    cohort: 'cohort_3',
  }),
  persona({
    slug: 'priya-raman',
    givenName: 'Priya',
    familyName: 'Raman',
    title: 'Forward Deployed operator',
    roles: ['forward_deployed_operator'],
    cohort: 'cohort_3',
  }),
  persona({
    slug: 'tomas-herrera',
    givenName: 'Tomás',
    familyName: 'Herrera',
    title: 'Forward Deployed operator',
    roles: ['forward_deployed_operator'],
    capabilityGrants: ['credential.debug'],
    directSend: true,
    cohort: 'cohort_10',
  }),
  persona({
    slug: 'lena-okafor',
    givenName: 'Lena',
    familyName: 'Okafor',
    title: 'Template editor',
    roles: ['template_editor'],
    cohort: 'cohort_10',
  }),
  persona({
    slug: 'marcus-whitfield',
    givenName: 'Marcus',
    familyName: 'Whitfield',
    title: 'Template publisher',
    roles: ['template_publisher'],
    cohort: 'cohort_10',
  }),
  persona({
    slug: 'dana-kowalski',
    givenName: 'Dana',
    familyName: 'Kowalski',
    title: 'Audit reviewer',
    roles: ['audit_reviewer'],
    cohort: 'all',
  }),
  persona({
    slug: 'reed-castellano',
    givenName: 'Reed',
    familyName: 'Castellano',
    title: 'Forward Deployed operator (suspended)',
    roles: ['forward_deployed_operator'],
    status: 'suspended',
    cohort: 'all',
  }),
  persona({
    slug: 'noor-haddad',
    givenName: 'Noor',
    familyName: 'Haddad',
    title: 'Forward Deployed operator (invited)',
    roles: ['forward_deployed_operator'],
    status: 'invited',
    cohort: 'all',
  }),
  persona({
    slug: 'felix-brandt',
    givenName: 'Felix',
    familyName: 'Brandt',
    title: 'Contractor (not invited)',
    roles: [],
    status: 'not_invited',
    invitedBySlug: null,
    cohort: 'all',
  }),
];

/** Personas that are Agency users (everything except the IdP-only persona). */
export const AGENCY_STAFF: readonly StaffPersona[] = STAFF_PERSONAS.filter((p) => p.status !== 'not_invited');

export const FOUNDING_ADMIN_SLUG = 'amir-tarighat';

export function findStaff(slugOrSubjectOrEmail: string): StaffPersona | undefined {
  const needle = slugOrSubjectOrEmail.toLowerCase();
  return STAFF_PERSONAS.find(
    (p) => p.slug === needle || p.subject.toLowerCase() === needle || p.email.toLowerCase() === needle,
  );
}

export function staffBySlug(slug: string): StaffPersona {
  const found = findStaff(slug);
  if (!found) throw new Error(`unknown staff persona: ${slug}`);
  return found;
}

/** Deterministic timestamps for the seed: invited/activated/suspended relative to FIXTURE_NOW. */
export const STAFF_TIMELINE_DAYS: Record<string, { invited: number; activated: number | null; suspended: number | null }> = {
  'amir-tarighat': { invited: 120, activated: 120, suspended: null },
  'priya-raman': { invited: 95, activated: 94, suspended: null },
  'tomas-herrera': { invited: 90, activated: 88, suspended: null },
  'lena-okafor': { invited: 60, activated: 59, suspended: null },
  'marcus-whitfield': { invited: 60, activated: 58, suspended: null },
  'dana-kowalski': { invited: 45, activated: 44, suspended: null },
  'reed-castellano': { invited: 80, activated: 79, suspended: 12 },
  'noor-haddad': { invited: 2, activated: null, suspended: null },
};
